import React, { useState, useEffect, useRef, useCallback } from "react";
import SEO from "../component/SEO";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { FiSend, FiArrowLeft, FiUser, FiSearch } from "react-icons/fi";

const AdminMessages = () => {
  const user = useSelector((state) => state.user);
  
  // Conversations State
  const [conversations, setConversations] = useState([]);
  const [convPage, setConvPage] = useState(1);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [hasMoreConv, setHasMoreConv] = useState(true);

  // Messages State
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgPage, setMsgPage] = useState(1);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMsg, setHasMoreMsg] = useState(true);
  
  // UI State
  const [replyText, setReplyText] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const sidebarContainerRef = useRef(null);
  
  // Detect Mobile View
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- CONVERSATIONS ---

  const fetchConversations = useCallback(async (pageNum = 1, isSilent = false) => {
    if (!isSilent) setLoadingConversations(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/conversations?page=${pageNum}&limit=20`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const data = await res.json();
      if (res.ok) {
        if (pageNum === 1) {
          // Detect changes to avoid flicker
          setConversations(prev => {
             // Simple check: if ID of first item is different, or length is different, update.
             // For better "no flicker", we could deep compare, but replacing list 1 usually is fine 
             // if we preserve scroll.
             // If we are polling, we only want to update if `lastMessageAt` changed for any top item.
             if (isSilent && prev.length > 0 && data.length > 0 && prev[0].lastMessageAt === data[0].lastMessageAt) {
                 return prev; 
             }
             return data;
          });
        } else {
            setConversations(prev => [...prev, ...data]);
        }
        
        if (data.length < 20) setHasMoreConv(false);
        else setHasMoreConv(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (!isSilent) setLoadingConversations(false);
    }
  }, [user.token]);

  // Initial Load & Polling for Conversations
  useEffect(() => {
    if (user?.token) {
        fetchConversations(1);
        const interval = setInterval(() => {
            fetchConversations(1, true); // Silent poll page 1 only
        }, 5000); 
        return () => clearInterval(interval);
    }
  }, [user?.token, fetchConversations]);

  const handleSidebarScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop <= clientHeight + 50 && hasMoreConv && !loadingConversations) {
          const nextPage = convPage + 1;
          setConvPage(nextPage);
          fetchConversations(nextPage);
      }
  };

  // --- MESSAGES ---

  const fetchMessages = async (email, pageNum = 1, isSilent = false) => {
    if (!isSilent) setLoadingMessages(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/messages/${email}?page=${pageNum}&limit=20`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const data = await res.json(); // Returns Newest First
      if (res.ok) {
        // We need to display chronological (Oldest -> Newest)
        // Data from backend: [Newest (0), ..., Older (19)]
        // Reverse it for display: [Older (19), ..., Newest (0)]
        const chronologicalData = [...data].reverse();

        if (pageNum === 1) {
             setMessages(chronologicalData);
             // if (!isSilent) scrollToBottom(); // Removed auto-scroll on load
        } else {
             // Prepend older messages
             // Maintain scroll position is tricky with standard React. 
             // We'll rely on user scrolling up implies they want to see history.
             const currentScrollHeight = chatContainerRef.current?.scrollHeight || 0;
             setMessages(prev => [...chronologicalData, ...prev]);
             
             // Restore scroll position
             requestAnimationFrame(() => {
                 if (chatContainerRef.current) {
                     chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight - currentScrollHeight;
                 }
             });
        }

        if (data.length < 20) setHasMoreMsg(false);
        else setHasMoreMsg(true);
      }
    } catch (err) {
        if (!isSilent) toast.error("Failed to load messages");
    } finally {
      if (!isSilent) setLoadingMessages(false);
    }
  };

  const handleUserClick = (conv) => {
    setSelectedUser(conv);
    setShowChatOnMobile(true);
    setMsgPage(1);
    setMessages([]); // Clear previous messages immediately
    setHasMoreMsg(true);
    fetchMessages(conv.email, 1);
  };

  // Poll active chat
  useEffect(() => {
    let interval;
    if (selectedUser?.email && user?.token) {
        interval = setInterval(() => {
            // Poll for new messages (Page 1)
            // We need to handle this carefully. If we just replace page 1, we might lose scroll context
            // if the user is scrolled up.
            // Ideally, we fetch page 1, see if there are NEW IDs not in our current state, and append them.
            // For now, let's keep it simple: Silent fetch page 1 ONLY if at bottom?
            // Or just check if latest message time changed?
            
            // Simplified: Fetch page 1.
            fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/messages/${selectedUser.email}?page=1&limit=20`, {
                headers: { Authorization: `Bearer ${user.token}` },
            })
            .then(res => res.json())
            .then(data => { // Data is Newest First
                if (data.length > 0) {
                     setMessages(prev => {
                         const newestReceived = data[0];
                         const currentNewest = prev[prev.length - 1];
                         
                         // If the newest received is newer than what we have, update.
                         // But we need to handle "gap" if we have 200 msgs and polled 20.
                         // Keeping it simple: If new message detected, append it.
                         
                         // Logic: find messages in `data` that seem newer than `currentNewest`
                         // Since `data` is sorted desc, start from 0.
                         if (!currentNewest) return [...data].reverse();
                         
                         const newItems = [];
                         for (let msg of data) {
                             if (new Date(msg.createdAt) > new Date(currentNewest.createdAt)) {
                                 newItems.unshift(msg); // Add to front of "newItems" to keep chronological
                             } else {
                                 break; // Found one we already have (or older)
                             }
                         }
                         
                         if (newItems.length > 0) {
                             // setTimeout(scrollToBottom, 100); // Removed auto-scroll on poll
                             return [...prev, ...newItems];
                         }
                         return prev;
                     });
                }
            })
            .catch(console.error);

        }, 3000);
    }
    return () => clearInterval(interval);
  }, [selectedUser, user?.token]);


  const scrollToBottom = () => {
    requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  };
  
  const handleChatScroll = (e) => {
      if (e.target.scrollTop === 0 && hasMoreMsg && !loadingMessages) {
          const nextPage = msgPage + 1;
          setMsgPage(nextPage);
          fetchMessages(selectedUser.email, nextPage, false); // Show loading indicator
      }
  };


  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;

    // Optimistic Update
    const newMessage = {
      _id: Date.now().toString(),
      message: replyText,
      sender: "admin",
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, newMessage]);
    setReplyText("");
    // scrollToBottom(); // Removed auto-scroll on reply

    try {
      window.location.href = `mailto:${selectedUser.email}?subject=Reply from Bakery &body=${replyText}`;

      await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/admin/reply`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          email: selectedUser.email,
          message: replyText,
          name: "Admin"
        }),
      });
      // Force refresh conv list to show update
      fetchConversations(1, true);
    } catch (err) {
      console.error("Failed to save reply", err);
      toast.error("Reply sent via mail but failed to save in history");
    }
  };

  const handleBackToSidebar = () => {
    setShowChatOnMobile(false);
    setSelectedUser(null);
  };

  // --- RENDER HELPERS ---
  
  const renderSidebar = () => (
    <div className={`flex flex-col h-full bg-white border-r border-gray-200 ${isMobileView && showChatOnMobile ? "hidden" : "w-full md:w-1/3 lg:w-1/4"}`}>
      <div className="p-4 border-b border-gray-200 bg-gray-50">
         <div className="flex justify-between items-center mb-0">
             <h2 className="text-xl font-bold text-gray-800">Messages</h2>
             {/* <span className="bg-pink-100 text-pink-600 px-2 py-1 rounded-full text-xs font-semibold">{conversations.length}</span> */}
         </div>
      </div>
      
      <div 
        ref={sidebarContainerRef}
        onScroll={handleSidebarScroll}
        className="flex-1 overflow-y-auto"
      >
        {conversations.length === 0 && !loadingConversations ? (
           <div className="p-4 text-center text-gray-500">No conversations yet.</div>
        ) : (
          conversations.map((conv) => (
            <div
              key={conv._id}
              onClick={() => handleUserClick(conv)}
              className={`flex items-center p-4 cursor-pointer transition hover:bg-gray-50 border-b border-gray-100 ${selectedUser?.email === conv.email ? "bg-pink-50 border-l-4 border-pink-500" : ""}`}
            >
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-red-400 flex items-center justify-center text-white font-bold text-lg mr-4 shrink-0 shadow-sm uppercase">
                {conv.name?.charAt(0) || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-baseline mb-1">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{conv.name || "Unknown User"}</h3>
                  <span className="text-xs text-gray-500">
                      {new Date(conv.lastMessageAt).toLocaleDateString() === new Date().toLocaleDateString() 
                          ? new Date(conv.lastMessageAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                          : new Date(conv.lastMessageAt).toLocaleDateString()}
                  </span>
                </div>
                <p className={`text-sm truncate text-gray-500`}>
                   {conv.latestMessage}
                </p>
              </div>
            </div>
          ))
        )}
        {loadingConversations && <div className="p-4 text-center text-xs text-gray-400">Loading...</div>}
      </div>
    </div>
  );

  const renderChatWindow = () => {
    if (!selectedUser) {
      return (
        <div className={`hidden md:flex flex-1 flex-col items-center justify-center bg-gray-50 h-full text-gray-400`}>
           <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mb-4">
             <FiSend size={40} className="text-gray-400 -ml-1 mt-1" />
           </div>
           <p className="text-lg">Select a conversation</p>
        </div>
      );
    }

    return (
      <div className={`flex flex-col h-full w-full bg-gray-50 ${isMobileView && !showChatOnMobile ? "hidden" : "flex-1"}`}>
        {/* Chat Header */}
        <div className="h-16 bg-white border-b border-gray-200 flex items-center px-4 shadow-sm z-10">
          {isMobileView && (
            <button onClick={handleBackToSidebar} className="mr-3 p-2 hover:bg-gray-100 rounded-full text-gray-600">
              <FiArrowLeft size={20} />
            </button>
          )}
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-indigo-400 flex items-center justify-center text-white font-bold text-sm mr-3 uppercase">
               {selectedUser.name?.charAt(0)}
            </div>
            <div>
              <h3 className="font-bold text-gray-900 leading-tight">{selectedUser.name}</h3>
              <p className="text-xs text-gray-500">{selectedUser.email}</p>
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div 
            ref={chatContainerRef}
            onScroll={handleChatScroll}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50"
        >
          {loadingMessages && msgPage === 1 && (
             <div className="text-center mt-10 text-gray-400">Loading history...</div>
          )}
          {loadingMessages && msgPage > 1 && (
             <div className="text-center text-xs text-gray-300 py-2">Loading older messages...</div>
          )}
          
          {messages.map((msg, idx) => {
              const isAdmin = msg.sender === "admin";
              return (
                <div key={msg._id || idx} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] md:max-w-[60%] rounded-2xl px-4 py-2 shadow-sm ${
                    isAdmin 
                      ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-br-none" 
                      : "bg-white text-gray-800 border border-gray-200 rounded-bl-none"
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>
                    <p className={`text-[10px] mt-1 text-right ${isAdmin ? "text-pink-100" : "text-gray-400"}`}>
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })
          }
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-white border-t border-gray-200">
          <form onSubmit={handleReply} className="flex items-center gap-2 max-w-4xl mx-auto">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type a reply..."
              className="flex-1 bg-gray-100 border-0 text-gray-800 rounded-full px-5 py-3 focus:ring-2 focus:ring-pink-500 focus:bg-white transition outline-none"
            />
            <button 
              type="submit" 
              disabled={!replyText.trim()}
              className="bg-pink-600 hover:bg-pink-700 text-white p-3 rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition transform active:scale-95"
            >
              <FiSend size={18} />
            </button>
          </form>
          <p className="text-xs text-center text-gray-400 mt-2">
            Reply will open your email client & be saved here.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-screen pt-0 bg-white overflow-hidden">
        <div className="w-full h-full flex"> 
            {renderSidebar()}
            {renderChatWindow()}
        </div>
    </div>
  );
};

export default AdminMessages;
